// An article as the web app keeps it. `meta` is exactly the synced meta JSON (SYNC.md, LISTENING.md, TAGS.md §2,
// IMAGES.md §2), kept whole so fields this app doesn't use survive a round trip through it.
import type { MetaTags } from "../tags/tags";
import type { ArticleImage } from "./images";

export interface Meta {
  sourceURL?: string | null; title?: string; siteName?: string | null; author?: string | null; excerpt?: string | null;
  sharedText?: string | null; text?: string | null; addedAt?: string; readyAt?: string | null;
  voiceName?: string | null; modelName?: string | null; chunks?: { index: number; text: string }[];
  skipped?: number[] | null; skipReasons?: string[] | null; quoteParagraphs?: number[] | null;
  tags?: MetaTags | null; images?: ArticleImage[] | null;
  [k: string]: unknown;
}

export interface Progress { chunk: number; offset: number; finished: boolean }

export interface Article {
  id: string;
  /** ready = in the library; fetching / failed = a link being added on this browser (never synced). */
  status: "ready" | "fetching" | "failed";
  error?: string;
  url?: string;
  meta: Meta;
  metaUpdatedAt: number;
  progress: Progress;
  progressUpdatedAt: number;
  /** The sync database has it. */
  synced: boolean;
  /** Last time this browser opened it (local only; "Last opened" order). */
  openedAt?: number;
  /** A book row from another device (EPUB books aren't supported on the web; kept only to skip it). */
  isBook?: boolean;
}

export const title = (a: Article) => a.meta.title || "Article";

export function host(a: Article): string | null {
  try {
    const h = new URL(a.meta.sourceURL || "").hostname;
    return h.startsWith("www.") ? h.slice(4) : h;
  } catch { return null; }
}

export const displaySource = (a: Article) => a.meta.siteName || host(a) || (a.meta.sharedText ? "Shared text" : "Unknown source");

/** Source paragraphs (LISTENING.md): (text ?? sharedText) split on blank lines, trimmed, empty ones dropped. */
export function sourceParagraphs(a: Article): string[] {
  return (a.meta.text ?? a.meta.sharedText ?? "").split("\n\n").map((p) => p.trim()).filter(Boolean);
}

export const chunks = (a: Article) => [...(a.meta.chunks ?? [])].sort((x, y) => x.index - y.index).map((c) => c.text);

export const addedAt = (a: Article) => Date.parse(a.meta.addedAt || "") || 0;

export const wordCount = (a: Article) => (a.meta.text ?? a.meta.sharedText ?? "").split(/\s+/).filter(Boolean).length;

export function uuid(): string {
  return crypto.randomUUID().toUpperCase();
}
